import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: 'About Us', href: '#about-us' },
  { label: 'Our Services', href: '#our-services' },
  { label: 'Our Products', href: '#our-products' },
  { label: 'Contact Us', href: '#contact-us' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const [scrolled, setScrolled] = useState(!isHome);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isHome) return;

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    if (!isHome) {
      navigate(`/${href}`);
      return;
    }

    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-nv-border'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="/"
            className={`text-lg md:text-xl font-semibold tracking-tight transition-colors duration-300 ${
              scrolled ? 'text-nv-black' : 'text-white'
            }`}
          >
            Nova Vertex
          </a>

          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={isHome ? item.href : `/${item.href}`}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`text-sm tracking-wide transition-colors duration-300 hover:opacity-70 ${
                  scrolled ? 'text-nv-black' : 'text-white'
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>

          <a
            href={isHome ? '#contact-us' : '/#contact-us'}
            onClick={(e) => handleNavClick(e, '#contact-us')}
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
              scrolled
                ? 'bg-nv-black text-white hover:bg-nv-gray'
                : 'bg-white text-nv-black hover:bg-white/90'
            }`}
          >
            Get in Touch
            <i className="ri-arrow-right-line text-sm" />
          </a>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center transition-colors ${
              scrolled ? 'text-nv-black' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`} />
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md border-b border-nv-border">
          <div className="px-6 py-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={isHome ? item.href : `/${item.href}`}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-sm text-nv-black py-2"
              >
                {item.label}
              </a>
            ))}
            <a
              href={isHome ? '#contact-us' : '/#contact-us'}
              onClick={(e) => handleNavClick(e, '#contact-us')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-nv-black text-white w-fit mt-2"
            >
              Get in Touch
              <i className="ri-arrow-right-line text-sm" />
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
